import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import {
  calcularPontosCiclo,
  calcularProgressao,
  calcularLimiarFuncionario,
  aplicaRegraEspecial18Anos,
  PONTOS_REGRA_18_ANOS,
  pontosParaAvaliacao,
  PONTOS_HISTORICO,
  type AvaliacaoSiadap,
  type PontosEscalasConfig,
} from '@/utils/progressaoUtils';

const avatarPalette = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatarPalette(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarPalette.length;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function fmt(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

const avalCfg: Record<string, { bg: string; text: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700'    },
};

const statusCfgMap = {
  pronto:       { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', bar: 'bg-emerald-500', label: 'Elegível'     },
  proximo:      { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   bar: 'bg-amber-400',   label: 'Próximo'      },
  em_progresso: { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-300',    label: 'Em Progresso' },
} as const;

type FuncionarioComDados = {
  funcionario: ReturnType<typeof useFuncionarios>['funcionarios'][number];
  pontosTotal: number;
  pontosDescontados: number;
  pontosSiadap: number;
  pontosHistorico: number;
  limiarEfetivo: number;
  regraEspecial: boolean;
  status: 'pronto' | 'proximo' | 'em_progresso';
  percentagem: number;
  totalAvaliacoes: number;
  ultimaAvaliacao: string | null;
  ultimoPeriodo: string | null;
};

function useDadosPorFuncionario() {
  const { funcionarios, loading } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, loadingSiadap } = useSiadap();
  const { settings } = useAppSettings();

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente:   settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom:         settings.pontos_bom,
    Regular:     settings.pontos_regular,
    Inadequado:  settings.pontos_inadequado,
  }), [settings]);

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente:   settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom:         settings.pontos_bom_2023,
      Regular:     settings.pontos_regular_2023,
      Inadequado:  settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:   settings.pontos_excelente_pre2023,
      Relevante:   settings.pontos_relevante_pre2023,
      Adequado:    settings.pontos_adequado_pre2023,
      Inadequado:  settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const dados = useMemo<FuncionarioComDados[]>(() => {
    return funcionarios.map((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const descontosProgressoes = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const descontosManuais = f.pontosDescontados ?? 0;
      let pontosDescontados: number;
      if (descontosManuais > descontosProgressoes) {
        pontosDescontados = descontosManuais;
      } else if (descontosManuais === descontosProgressoes) {
        pontosDescontados = descontosProgressoes;
      } else {
        pontosDescontados = descontosProgressoes + descontosManuais;
      }
      const pontosManuais = f.pontosAtuais ?? 0;

      const pontosTotal = calcularPontosCiclo(siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, undefined, escalas);
      const { limiar } = calcularLimiarFuncionario(siadapDoFunc, historicoDoFunc, settings.pontos_progressao || 8, settings.pontos_progressao_pre2025 || 10, settings.pontos_progressao_2023 || 8, escalas);
      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      const regraEspecial = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, pontosTotal, jaProgrediu, 2024, limiar);
      const limiarEfetivo = regraEspecial ? PONTOS_REGRA_18_ANOS : limiar;
      const prog = calcularProgressao(pontosTotal, f.nivelAtual, limiarEfetivo);

      const pontosSiadap = siadapDoFunc.reduce((s, r) => s + pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas), 0);
      const pontosHistorico = historicoDoFunc.reduce((s, r) => s + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0), 0);

      const ultimaAvaliacao = [...siadapDoFunc].sort((a, b) => {
        const anoA = parseInt((a.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        const anoB = parseInt((b.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
        return anoB - anoA;
      })[0];

      return {
        funcionario: f,
        pontosTotal,
        pontosDescontados,
        pontosSiadap,
        pontosHistorico,
        limiarEfetivo,
        regraEspecial,
        status: prog.status as 'pronto' | 'proximo' | 'em_progresso',
        percentagem: prog.percentagem,
        totalAvaliacoes: siadapDoFunc.length + historicoDoFunc.length,
        ultimaAvaliacao: ultimaAvaliacao?.avaliacao ?? null,
        ultimoPeriodo: ultimaAvaliacao?.periodoAvaliacao ?? null,
      };
    });
  }, [funcionarios, registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, pontosMap, escalas, settings]);

  const isLoading = loading || loadingSiadap;
  return { dados, isLoading, settings };
}

function getGeneroLabel(func: FuncionarioComDados['funcionario']) {
  const g = func.genero?.trim();
  if (g) {
    if (g.toLowerCase().startsWith('m')) return 'Masculino';
    if (g.toLowerCase().startsWith('f')) return 'Feminino';
    return g;
  }
  return func.sexo === 'M' ? 'Masculino' : func.sexo === 'F' ? 'Feminino' : 'Não Especificado';
}

function getGeneroKey(func: FuncionarioComDados['funcionario']) {
  const g = func.genero?.trim().toLowerCase();
  if (g) {
    if (g.startsWith('m')) return 'Masculino';
    if (g.startsWith('f')) return 'Feminino';
    return g;
  }
  return func.sexo === 'M' ? 'Masculino' : func.sexo === 'F' ? 'Feminino' : 'Não Especificado';
}

export default function RelatorioGeneroPage() {
  const navigate = useNavigate();
  const { dados, isLoading, settings } = useDadosPorFuncionario();
  const printRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [generoFilter, setGeneroFilter] = useState('');
  const [expandedGeneros, setExpandedGeneros] = useState<Set<string>>(new Set());

  const generos = useMemo(() => {
    const gens = Array.from(new Set(dados.map((d) => getGeneroKey(d.funcionario)))).sort();
    return gens;
  }, [dados]);

  const dadosPorGenero = useMemo(() => {
    const grupos: Record<string, FuncionarioComDados[]> = {};
    dados.forEach((d) => {
      const gen = getGeneroKey(d.funcionario);
      if (!grupos[gen]) grupos[gen] = [];
      grupos[gen].push(d);
    });
    generos.forEach((g) => {
      if (!grupos[g]) grupos[g] = [];
    });
    return Object.entries(grupos).sort(([, a], [, b]) => b.length - a.length);
  }, [dados, generos]);

  const filtered = useMemo(() => {
    let list = dados;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((d) => d.funcionario.nome.toLowerCase().includes(q) || d.funcionario.id.includes(q));
    }
    if (generoFilter) {
      list = list.filter((d) => getGeneroKey(d.funcionario) === generoFilter);
    }
    return list;
  }, [dados, search, generoFilter]);

  const porGeneroFiltrado = useMemo(() => {
    const grupos: Record<string, FuncionarioComDados[]> = {};
    filtered.forEach((d) => {
      const gen = getGeneroKey(d.funcionario);
      if (!grupos[gen]) grupos[gen] = [];
      grupos[gen].push(d);
    });
    generos.forEach((g) => {
      if (!grupos[g]) grupos[g] = [];
    });
    return Object.entries(grupos)
      .filter(([, funcs]) => funcs.length > 0 || generoFilter === '')
      .sort(([, a], [, b]) => b.length - a.length);
  }, [filtered, generos, generoFilter]);

  // Estatísticas globais
  const statsGlobal = useMemo(() => {
    const total = dados.length;
    const masc = dados.filter((d) => getGeneroKey(d.funcionario) === 'Masculino').length;
    const fem = dados.filter((d) => getGeneroKey(d.funcionario) === 'Feminino').length;
    const outro = total - masc - fem;
    return { total, masc, fem, outro, pctMasc: total > 0 ? (masc / total) * 100 : 0, pctFem: total > 0 ? (fem / total) * 100 : 0 };
  }, [dados]);

  function toggleGenero(g: string) {
    setExpandedGeneros((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  function expandAll() {
    setExpandedGeneros(new Set(porGeneroFiltrado.map(([g]) => g)));
  }

  function collapseAll() {
    setExpandedGeneros(new Set());
  }

  function handleExportPdf() {
    window.print();
  }

  const generoIcon = (gen: string) => {
    if (gen === 'Masculino') return 'ri-men-line';
    if (gen === 'Feminino') return 'ri-women-line';
    return 'ri-user-line';
  };

  const generoColor = (gen: string) => {
    if (gen === 'Masculino') return 'bg-sky-50 text-sky-600';
    if (gen === 'Feminino') return 'bg-rose-50 text-rose-600';
    return 'bg-zinc-100 text-zinc-600';
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-area { box-shadow: none !important; }
        }
      `}</style>

      <main className="flex-1 bg-slate-50/60">
        {/* PAGE HEADER */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5 no-print">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/relatorios')}
                className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:text-zinc-800 hover:border-zinc-300 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-base" />
              </button>
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-team-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Análise</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Relatório por Género</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Distribuição de funcionários e elegibilidade por género — Masculino / Feminino</p>
              </div>
            </div>
            {!isLoading && filtered.length > 0 && (
              <button
                onClick={handleExportPdf}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-sm" /></div>
                Exportar PDF
              </button>
            )}
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">

          {/* RESUMO GLOBAL */}
          {!isLoading && statsGlobal.total > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 print-area">
              <div className="bg-white rounded-xl border border-zinc-100 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-100">
                    <i className="ri-team-line text-zinc-600 text-lg" />
                  </div>
                  <div>
                    <p className="text-[22px] font-bold text-zinc-900 leading-tight">{statsGlobal.total}</p>
                    <p className="text-[11px] font-medium text-zinc-500">Total de Funcionários</p>
                  </div>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-sky-400" style={{ width: `${statsGlobal.pctMasc}%` }} />
                  <div className="h-full bg-rose-400" style={{ width: `${statsGlobal.pctFem}%` }} />
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px]">
                  <span className="text-sky-600 font-semibold">{statsGlobal.pctMasc.toFixed(0)}% Masculino</span>
                  <span className="text-rose-600 font-semibold">{statsGlobal.pctFem.toFixed(0)}% Feminino</span>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-zinc-100 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-sky-50">
                    <i className="ri-men-line text-sky-600 text-lg" />
                  </div>
                  <div>
                    <p className="text-[22px] font-bold text-sky-700 leading-tight">{statsGlobal.masc}</p>
                    <p className="text-[11px] font-medium text-zinc-500">Masculino</p>
                  </div>
                </div>
                {(() => {
                  const funcs = dados.filter((d) => getGeneroKey(d.funcionario) === 'Masculino');
                  const eleg = funcs.filter((d) => d.status === 'pronto').length;
                  const prox = funcs.filter((d) => d.status === 'proximo').length;
                  const media = funcs.length > 0 ? funcs.reduce((s, d) => s + d.pontosTotal, 0) / funcs.length : 0;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Elegíveis</span>
                        <span className="font-semibold text-emerald-700">{eleg} ({funcs.length > 0 ? ((eleg / funcs.length) * 100).toFixed(0) : 0}%)</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Próximos</span>
                        <span className="font-semibold text-amber-700">{prox}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Média Pontos</span>
                        <span className="font-semibold text-zinc-700">{fmt(media)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="bg-white rounded-xl border border-zinc-100 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-rose-50">
                    <i className="ri-women-line text-rose-600 text-lg" />
                  </div>
                  <div>
                    <p className="text-[22px] font-bold text-rose-700 leading-tight">{statsGlobal.fem}</p>
                    <p className="text-[11px] font-medium text-zinc-500">Feminino</p>
                  </div>
                </div>
                {(() => {
                  const funcs = dados.filter((d) => getGeneroKey(d.funcionario) === 'Feminino');
                  const eleg = funcs.filter((d) => d.status === 'pronto').length;
                  const prox = funcs.filter((d) => d.status === 'proximo').length;
                  const media = funcs.length > 0 ? funcs.reduce((s, d) => s + d.pontosTotal, 0) / funcs.length : 0;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Elegíveis</span>
                        <span className="font-semibold text-emerald-700">{eleg} ({funcs.length > 0 ? ((eleg / funcs.length) * 100).toFixed(0) : 0}%)</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Próximos</span>
                        <span className="font-semibold text-amber-700">{prox}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">Média Pontos</span>
                        <span className="font-semibold text-zinc-700">{fmt(media)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* FILTROS */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 space-y-3 no-print">
            <div className="flex gap-2.5 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar por nome ou nº de funcionário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
              </div>
              <select
                value={generoFilter}
                onChange={(e) => setGeneroFilter(e.target.value)}
                className={`text-[13px] border rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer min-w-[180px] ${generoFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}
              >
                <option value="">Todos os Géneros</option>
                {generos.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[12px] text-zinc-400">
                <span className="font-semibold text-zinc-600">{filtered.length}</span> funcionário(s)
                {porGeneroFiltrado.length > 0 && ` · ${porGeneroFiltrado.filter(([, f]) => f.length > 0).length} género(s)`}
              </span>
              {(search || generoFilter) && (
                <button
                  onClick={() => { setSearch(''); setGeneroFilter(''); }}
                  className="whitespace-nowrap flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
                >
                  <i className="ri-close-circle-line text-sm" />Limpar filtros
                </button>
              )}
            </div>
          </div>

          {/* CONTROLES */}
          {!isLoading && porGeneroFiltrado.length > 0 && (
            <div className="flex items-center justify-end gap-2 no-print">
              <button onClick={expandAll} className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer flex items-center gap-1">
                <i className="ri-expand-diagonal-line text-sm" />Expandir tudo
              </button>
              <span className="text-zinc-200">·</span>
              <button onClick={collapseAll} className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer flex items-center gap-1">
                <i className="ri-collapse-diagonal-line text-sm" />Recolher tudo
              </button>
            </div>
          )}

          {/* CONTEÚDO */}
          {isLoading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
              <p className="text-[13px] text-zinc-400">A carregar relatório...</p>
            </div>
          ) : porGeneroFiltrado.filter(([, f]) => f.length > 0).length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center"><i className="ri-search-line text-4xl text-zinc-200" /></div>
              <p className="text-[13px] font-medium text-zinc-400">Nenhum funcionário encontrado</p>
            </div>
          ) : (
            <div className="space-y-3 print-area" ref={printRef}>
              {porGeneroFiltrado.map(([genero, funcs]) => {
                if (funcs.length === 0) return null;
                const isExpanded = expandedGeneros.has(genero);
                const elegiveis = funcs.filter((d) => d.status === 'pronto').length;
                const proximos = funcs.filter((d) => d.status === 'proximo').length;
                const emProgresso = funcs.filter((d) => d.status === 'em_progresso').length;
                const mediaPontos = funcs.length > 0 ? funcs.reduce((s, d) => s + d.pontosTotal, 0) / funcs.length : 0;
                const pctElegiveis = funcs.length > 0 ? (elegiveis / funcs.length) * 100 : 0;
                const pctProximos = funcs.length > 0 ? (proximos / funcs.length) * 100 : 0;
                const pctProgresso = funcs.length > 0 ? (emProgresso / funcs.length) * 100 : 0;

                return (
                  <div key={genero} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                    <button
                      onClick={() => toggleGenero(genero)}
                      className="whitespace-nowrap w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-zinc-50/60 transition-colors cursor-pointer no-print"
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${generoColor(genero)}`}>
                        <i className={`${generoIcon(genero)} text-base`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-[14px] font-bold text-zinc-800 truncate">{genero}</h3>
                          <span className="text-[11px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-semibold">{funcs.length} func.</span>
                          {elegiveis > 0 && (
                            <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                              {elegiveis} elegíve{elegiveis === 1 ? 'l' : 'is'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden flex">
                            {pctElegiveis > 0 && <div className="h-full bg-emerald-500" style={{ width: `${pctElegiveis}%` }} />}
                            {pctProximos > 0 && <div className="h-full bg-amber-400" style={{ width: `${pctProximos}%` }} />}
                            {pctProgresso > 0 && <div className="h-full bg-zinc-200" style={{ width: `${pctProgresso}%` }} />}
                          </div>
                          <span className="text-[10px] text-zinc-400 flex-shrink-0">Média: <strong className="text-zinc-600">{fmt(mediaPontos)} pts</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-2 text-[11px]">
                          {elegiveis > 0 && <span className="text-emerald-700 font-semibold">{elegiveis} ✓</span>}
                          {proximos > 0 && <span className="text-amber-600">{proximos} ~</span>}
                          <span className="text-zinc-400">{emProgresso} ···</span>
                        </div>
                        <div className="w-6 h-6 flex items-center justify-center text-zinc-400">
                          <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
                        </div>
                      </div>
                    </button>

                    <div className="hidden print:block px-5 py-3 border-b border-zinc-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-[14px] font-bold text-zinc-800">{genero}</h3>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span>{funcs.length} funcionários</span>
                          <span className="text-emerald-700 font-semibold">{elegiveis} elegíveis</span>
                          <span>Média: {fmt(mediaPontos)} pts</span>
                        </div>
                      </div>
                    </div>

                    {(isExpanded) && (
                      <div className="border-t border-zinc-100">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-zinc-50/80 border-b border-zinc-100">
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Funcionário</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Carreira / Unidade</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Última Aval.</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide w-36">Progressão</th>
                              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                              <th className="px-5 py-2.5 text-right text-[10px] font-semibold text-zinc-400 uppercase tracking-wide no-print">Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {funcs.map((d, i) => {
                              const f = d.funcionario;
                              const palette = getAvatarPalette(f.id);
                              const initials = getInitials(f.nome);
                              const sc = statusCfgMap[d.status];
                              const aval = d.ultimaAvaliacao ? (avalCfg[d.ultimaAvaliacao] ?? avalCfg['Adequado']) : null;
                              return (
                                <tr key={f.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer group ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}
                                  onClick={() => navigate(`/funcionarios/${f.id}/relatorio`)}>
                                  <td className="px-5 py-3">
                                    <div className="flex items-center gap-2.5">
                                      <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>{initials}</div>
                                      <div>
                                        <p className="text-[12px] font-semibold text-zinc-800">{f.nome}</p>
                                        <p className="text-[10px] text-zinc-400 font-mono">#{f.id}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="text-[11px] font-medium text-zinc-700">{f.carreira}</p>
                                    <p className="text-[10px] text-zinc-400 truncate max-w-[140px]">{f.unidadeOrganica || '—'}</p>
                                  </td>
                                  <td className="px-5 py-3">
                                    {d.ultimaAvaliacao && aval ? (
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${aval.bg} ${aval.text}`}>{d.ultimaAvaliacao}</span>
                                    ) : (
                                      <span className="text-[11px] text-zinc-300 italic">—</span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3">
                                    <span className="text-[13px] font-bold text-zinc-800">{fmt(d.pontosTotal)}</span>
                                    <span className="text-[10px] text-zinc-400 ml-1">/ {d.limiarEfetivo}</span>
                                  </td>
                                  <td className="px-5 py-3">
                                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden w-28">
                                      <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${d.percentagem}%` }} />
                                    </div>
                                    <span className={`text-[10px] font-medium ${sc.text} mt-0.5 block`}>{d.percentagem.toFixed(0)}%</span>
                                  </td>
                                  <td className="px-5 py-3">
                                    <div className="flex items-center gap-1">
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                                      <span className={`text-[11px] font-semibold ${sc.text}`}>{sc.label}</span>
                                      {d.regraEspecial && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full font-bold ml-1">18a</span>}
                                    </div>
                                  </td>
                                  <td className="px-5 py-3 text-right no-print">
                                    <button onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${f.id}/relatorio`); }}
                                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer ml-auto">
                                      <i className="ri-file-chart-line text-sm" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-zinc-50 border-t border-zinc-100">
                              <td colSpan={3} className="px-5 py-2.5 text-[11px] text-zinc-400 font-medium">
                                {funcs.length} funcionário(s) · Média {fmt(mediaPontos)} pts
                              </td>
                              <td className="px-5 py-2.5">
                                <span className="text-[11px] font-semibold text-zinc-600">{fmt(funcs.reduce((s, d) => s + d.pontosTotal, 0))} pts total</span>
                              </td>
                              <td colSpan={3} className="px-5 py-2.5">
                                <div className="flex items-center gap-3 text-[11px]">
                                  {elegiveis > 0 && <span className="text-emerald-700 font-semibold">{elegiveis} elegíveis</span>}
                                  {proximos > 0 && <span className="text-amber-600">{proximos} próximos</span>}
                                  <span className="text-zinc-400">{emProgresso} em progresso</span>
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="flex items-center justify-between text-[11px] text-zinc-400 pb-2 no-print">
              <span>SIADAP para RH · {settings.nome_camara}</span>
              <span>Atualizado em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
          )}
        </div>
      </main>
    </>
  );
}